/* ═══════════════════════════════════════════════════════════════════════════════
   AE QUOTA DEFERRED REVENUE ENGINE (v2)
   Computes GAAP revenue recognition, deferred revenue balance, and cash received
   from monthly new ACV (annual contracts paid upfront, recognized over 12 months).
   Supports churn/renewal/existing mode.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { recognizeExistingBook } from './existingBookRecognition';
import { recognizeForwardCohort, computeRenewalLookback } from './drRecognition';
import { termsActive, computeBillingTermsDR } from './billingTerms';   // billing terms P2: the per-term engine (one owner)

const r2 = n => Math.round(n * 100) / 100;

/**
 * @param {Object} monthlyNewACV   — { "2026-01": 50000, ... } from AE quota engine
 * @param {Object} settings        — ae_quota_settings row from DB
 * @param {string[]} forecastMonths — the forecast months of the caller's window (every month after the close)
 * @param {string} closeMonth      — last actuals month e.g. "2025-12"
 * @returns {{ newACV, grossRenewals, churn, netRenewals, totalACV, gaapRevenue, deferredBalance, cashReceived, schedule }}
 */
function computeAEQuotaDR(monthlyNewACV, settings, forecastMonths, closeMonth, closeUnbilledAR) {
  settings = settings || {};
  /* BILLING TERMS P2 (2026-10-26): a company that stores billing terms (a mix, per-term churn / renewal mode, renewal adjustments,
     Unbilled-AR collections or the link -- beyond their off forms) is computed by the per-term engine, engines/billingTerms.js.
     Every other company runs the code below UNTOUCHED (termsActive is false when all four columns are NULL or off). The 5th
     argument is the linked Unbilled-AR line's close balance; absent = 0, so every existing caller is unchanged. */
  if (termsActive(settings)) return computeBillingTermsDR(monthlyNewACV, settings, forecastMonths, closeMonth, closeUnbilledAR);
  const startMode = settings.start_mode || 'zero';
  const renewalMode = settings.renewal_mode || 'auto';
  const churnRate = parseFloat(settings.churn_rate) || 0;
  const manualRenewals = settings.manual_renewals || {};
  const openingBsBalance = parseFloat(settings.opening_bs_balance) || 0;
  const existingTotalACV = settings.existing_total_acv || {};
  const existingMonthlyRev = settings.existing_monthly_rev || {};

  /* ★★ THE SPAN IS NOW THE CALLER'S WINDOW. It used to be a fixed 24-month list:
         const ALL_24 = []; for (let i = 0; i < 24; i++) ALL_24.push(addMonths(fm0, i));
     — anchored on forecastMonths[0] and bounded by the LITERAL 24, not by the window. Every
     internal step iterated that list while the emit loop iterated the caller's forecastMonths
     and wrote `x[m] || 0`, so a window longer than 24 months found NO KEY and rendered $0.00.
     ⛔ THIS IS B6's DEFECT AT A DIFFERENT NUMBER — the identical silent `|| 0`, retired at 12
       on 2026-09-10 and here at 24. MEASURED BY EXECUTION: @17 and @24 correct, MONTH 25
       COLLAPSES TO $0.00 on all three DR engines, @30 six zero months — and the balance sheet
       still checked to $0.00 in all 60 months, because the sweep absorbs it into the cash plug.
     ⇒ ⛔ IT IS NOT REPLACED BY A LARGER LITERAL. 36, 60 or 120 is the same defect postponed,
       and this project has now paid for that shape three times.
     ★ WHY forecastMonths IS THE RIGHT SPAN, AND WHY DROPPING THE TAIL CHANGES NOTHING:
       · NO STEP READS A MONTH BEYOND THE WINDOW. The renewal lookback goes BACKWARD
         (addMonths(m, -12)), landing in actuals or inside the window.
       · recognizeForwardCohort WRITES forward for 12 months but is guarded by
         `if (gaapRevenue[recMonth] !== undefined)`, so writes past the keyspace are skipped —
         and those months were never emitted anyway.
       · every accumulator (cumNewACV, cumChurn, the deferred-balance roll) runs in ASCENDING
         order, so a month's value depends only on EARLIER months.
       ⇒ the old list started at the same fm0, so forecastMonths was a strict PREFIX of it;
         this drops a SUFFIX that nothing reads. The keyspace now EQUALS the emit space.
     ⓘ recognizeExistingBook needs NO change: its perpetual branch fills whatever list it is
       given, its conserving branch iterates a fixed RECOGNITION_TERM from months[0] (and is
       unreached — no live caller has passed conserveTotal since B6), and its decay branch is
       per-month over cohorts. All three are span-insensitive. */

  // Internal arrays keyed by month
  const newACV = {};
  const grossRenewals = {};
  const churn = {};
  const netRenewals = {};
  const totalACV = {};
  const gaapRevenue = {};
  const deferredBalance = {};
  const cashReceived = {};
  const schedule = [];

  // Initialize every forecast month to zero
  forecastMonths.forEach(m => {
    newACV[m] = 0;
    grossRenewals[m] = 0;
    churn[m] = 0;
    netRenewals[m] = 0;
    totalACV[m] = 0;
    gaapRevenue[m] = 0;
    deferredBalance[m] = 0;
    cashReceived[m] = 0;
  });

  // ── Step 1: Populate newACV ──
  forecastMonths.forEach(m => {
    if (m <= closeMonth) return; // never touch actuals
    // The month's booked new ACV, as the caller built it. A month with NO key books 0 -- never the month 12 earlier.
    // (P0, 2026-10-26: this used to fall back to monthlyNewACV[m - 12] -- "months 13-24: shift back 12". buildMonthlyNewACV
    // writes no key after a rep's termination month, so once every rep had left, a month 12 later re-booked the bookings of
    // 12 months earlier: phantom new ACV, cash and deferred revenue, and a KPI ARR step-up. Unreachable on a 9-month window;
    // live on any window longer than 12 forecast months. diffs/ghost-booking-build.txt.)
    newACV[m] = r2(monthlyNewACV[m] || 0);
  });

  // ── Step 2: Build totalACV iteratively (needs renewals which need prior totalACV) ──
  // Shared renewal-lookback primitive. The caller writes totalACV[m] back into its map
  // AFTER the call so the next iteration's auto lookback (totalACV[m-12]) chain holds.
  const forecastSold = {};   // Q-B2: the forecast-sold track (drRecognition.computeRenewalLookback)
  forecastMonths.forEach(m => {
    if (m <= closeMonth) return;
    const r = computeRenewalLookback({ month: m, renewalMode, manualRenewals, existingTotalACV, totalACV, churnRate, newACV, closeMonth, startMode, forecastSold });
    grossRenewals[m] = r.grossRenewals;
    churn[m] = r.churn;
    netRenewals[m] = r.netRenewals;
    totalACV[m] = r.totalACV;
    forecastSold[m] = r.forecastSoldValue;
  });

  // ── Step 3: GAAP Revenue recognition ──
  // In zero mode, recognize from totalACV — which ALREADY INCLUDES renewals, so each
  // renewal opens a fresh 12-month cohort and recognition continues indefinitely.
  // In existing mode, recognize from newACV — see the PERPETUAL note below.
  //
  // ★★ B6 COMMIT A — A CORRECT RULE HAD BEEN APPLIED ONE SCOPE TOO WIDE.
  // Step 3b(2) says renewals are not recognized because "the flat baseline already
  // assumes all renew". That is TRUE OF THE EXISTING BOOK and FALSE of new ACV booked
  // DURING the forecast — the baseline is the close-month figure and cannot contain a
  // contract written after it. So a new-ACV cohort expired at month 12 and its renewal
  // recognized NOTHING: measured −33,333.33/month from month 13, −200,000.00 by month 18.
  //
  // ⓘ THE TELL THAT THIS WAS A BUG AND NOT A DESIGN: the two start modes DISAGREED.
  //   'zero' recognizes totalACV (renewals included) and continues forever; 'existing'
  //   recognized newACV only and stopped. Same engine, same contracts, two answers.
  //
  // ★ THE OPERATOR'S MODEL (Def_Rev_template_annual_ver4 r18): annual contracts renew IN
  //   PERPETUITY until they churn, and recognition is SUM(all new ACV to date)/12 —
  //   CUMULATIVE, never rolling off. Only churn (r17) removes anything.
  //
  // ⛔ WHY CUMULATIVE AND NOT A PERPETUAL COHORT: they are not the same number.
  //   Σ r2(acv/12) drifts against r2(Σacv/12) by a cent every three months — measured
  //   −0.04 by month 11 on AE, which is exactly the residual this build removes from
  //   months 1-11. The template says Σ/12; this is Σ/12.
  //
  // ⛔ NOT DOUBLE-COUNTED: the EXISTING book's renewals are still not recognized (3b(2)
  //   is unchanged), and totalACV / deferredBalance / cashReceived are untouched.
  const displayOnly = {};                       // cohort display accumulator, never read
  forecastMonths.forEach(m => { displayOnly[m] = 0; });
  forecastMonths.forEach(m => {
    if (m <= closeMonth) return;
    const acv = startMode === 'existing' ? newACV[m] : totalACV[m];
    if (acv === 0) return;
    // The schedule display stays cohort-shaped (the DefRev page renders it); in existing
    // mode its writes go to a throwaway accumulator so the cumulative form below owns
    // gaapRevenue. In zero mode nothing changes at all.
    const target = startMode === 'existing' ? displayOnly : gaapRevenue;
    const { monthlyRecognition, recognized, remaining } = recognizeForwardCohort(acv, m, target);
    schedule.push({ saleMonth: m, acv, monthlyRecognition, recognized, remaining });
  });

  // ★ B6 COMMIT A — the PERPETUAL new-ACV stream (template r18), the mirror image of the
  // cumulative churn-of-book in Step 3b(3). Booked ACV keeps recognizing after its first
  // year because the contract renews; churn is what removes it.
  if (startMode === 'existing') {
    let cumNewACV = 0;
    forecastMonths.forEach(m => {
      if (m <= closeMonth) return;
      cumNewACV = r2(cumNewACV + (newACV[m] || 0));
      if (gaapRevenue[m] !== undefined) {
        gaapRevenue[m] = r2(gaapRevenue[m] + r2(cumNewACV / 12));
      }
    });
  }

  // ── Step 3b: Existing-book recognition (existing mode only) ──
  // SC-2 switchover: when existing_monthly_rev[closeMonth] is populated, the existing
  // book is held FLAT at that run-rate (a renewing book — "assume all renew"),
  // renewals are NOT recognized into revenue (they flow to totalACV → deferred only,
  // Step 2/4), and a forecast-only cumulative churn-of-book reduces revenue (the
  // Def_Rev_template_annual_ver4 row-16/17/20 mechanic). When NOT populated, fall
  // back to the legacy trailing-cohort decay + renewal recognition (no regression —
  // byte-identical to pre-SC-2 for any existing-mode tenant without a run-rate).
  if (startMode === 'existing') {
    const rawRunRate = existingMonthlyRev[closeMonth];
    const hasMonthlyRev = rawRunRate != null && rawRunRate !== '' && !Number.isNaN(parseFloat(rawRunRate));

    if (hasMonthlyRev) {
      // (1) Existing book → FLAT (perpetual) at the most-recent-closed-month run-rate.
      const closeRunRate = r2(parseFloat(rawRunRate) || 0);
      const ebRev = recognizeExistingBook({
        renewing: true,
        forecastMonths: forecastMonths,
        closeMonth,
        closeRunRate, // NO conserveTotal → perpetual flat (the book renews forever)
      });
      forecastMonths.forEach(fm => {
        if (fm <= closeMonth) return;
        if (gaapRevenue[fm] !== undefined) {
          gaapRevenue[fm] = r2(gaapRevenue[fm] + ebRev[fm]);
        }
      });

      // (2) Renewals are intentionally NOT recognized into revenue — they already
      //     flow into totalACV (Step 2) → deferredBalance (Step 4) / cashReceived.

      // (3) Churn-of-book: forecast-only cumulative churn, /12, reduces revenue.
      //     Accumulation starts at the FIRST forecast month (the flat anchor already
      //     embeds historical churn). churn[fm] (Step 2 = grossRenewals[fm]×churnRate)
      //     is non-negative, so subtracting cumChurn/12 reduces revenue.
      // ⓘ CROSS-REF (updated by B6 COMMIT B): this used to warn "do NOT unify with CQ's
      //   cohort form without a bookShape param". THAT WARNING IS SPENT — Company Quota no
      //   longer has a cohort form. It now calls THIS engine (computeCompanyQuotaDR Step 3),
      //   so both modules share this one cumulative, perpetual-book shape. There is nothing
      //   left to unify and no bookShape param is needed.
      // ★ B6 COMMIT A — this iterated `forecastMonths` while the flat book above uses
      // forecastMonths, so on a horizon longer than the caller's window the book kept being
      // recognized while its churn silently stopped. Both now span the same horizon.
      let cumChurn = 0;
      forecastMonths.forEach(fm => {
        if (fm <= closeMonth) return;
        cumChurn = r2(cumChurn + (churn[fm] || 0));
        const churnOfBook = r2(cumChurn / 12);
        if (gaapRevenue[fm] !== undefined) {
          gaapRevenue[fm] = r2(gaapRevenue[fm] - churnOfBook);
        }
      });
    } else {
      // ── Fallback (legacy, no existing_monthly_rev): trailing-cohort DECAY ──
      // Each actuals month's existing_total_acv recognizes ACV/12 over a 12-month
      // window from that actuals month; only forecast months in-window contribute.
      const actualsMonths = Object.keys(existingTotalACV).filter(m => m <= closeMonth).sort();
      forecastMonths.forEach(fm => {
        if (fm <= closeMonth) return;
        const [fy, fmo] = fm.split("-").map(Number);
        actualsMonths.forEach(am => {
          const acv = r2(parseFloat(existingTotalACV[am]) || 0);
          if (acv === 0) return;
          const [ay, amo] = am.split("-").map(Number);
          const monthsElapsed = (fy - ay) * 12 + (fmo - amo) + 1; // 1-based
          if (monthsElapsed > 12 || monthsElapsed < 1) return; // outside 12-month window
          const monthlyRec = r2(acv / 12);
          const recAmt = monthsElapsed < 12 ? monthlyRec : r2(acv - r2(monthlyRec * 11));
          gaapRevenue[fm] = r2(gaapRevenue[fm] + recAmt);
        });
      });

      // Legacy renewal cohort recognition: each forecast month's netRenewals is a
      // cohort recognized 1/12 over 12 months (shared primitive; no schedule push).
      forecastMonths.forEach(m => {
        if (m <= closeMonth) return;
        const renAcv = netRenewals[m];
        if (renAcv === 0) return;
        recognizeForwardCohort(renAcv, m, gaapRevenue);
      });
    }
  }

  // ── Step 4: Deferred Revenue Balance ──
  if (startMode === 'existing') {
    // First forecast month picks up from openingBsBalance
    let bal = openingBsBalance;
    forecastMonths.forEach(m => {
      if (m <= closeMonth) return;
      bal = r2(bal + totalACV[m] - gaapRevenue[m]);
      deferredBalance[m] = bal;
    });
  } else {
    // Zero start mode
    let bal = 0;
    forecastMonths.forEach(m => {
      if (m <= closeMonth) return;
      bal = r2(bal + totalACV[m] - gaapRevenue[m]);
      deferredBalance[m] = bal;
    });
  }

  // ── Step 5: Cash received = totalACV (annual upfront) ──
  forecastMonths.forEach(m => {
    if (m <= closeMonth) return;
    cashReceived[m] = totalACV[m];
  });

  // ── Build output: only forecastMonths ──
  const out = {
    newACV: {},
    grossRenewals: {},
    churn: {},
    netRenewals: {},
    totalACV: {},
    gaapRevenue: {},
    deferredBalance: {},
    cashReceived: {},
    schedule,
  };

  forecastMonths.forEach(m => {
    out.newACV[m] = newACV[m] || 0;
    out.grossRenewals[m] = grossRenewals[m] || 0;
    out.churn[m] = r2(-(churn[m] || 0)); // negative display
    out.netRenewals[m] = netRenewals[m] || 0;
    out.totalACV[m] = totalACV[m] || 0;
    out.gaapRevenue[m] = gaapRevenue[m] || 0;
    out.deferredBalance[m] = deferredBalance[m] || 0;
    out.cashReceived[m] = cashReceived[m] || 0;
  });

  return out;
}

export { computeAEQuotaDR, r2 };
